import {useCallback} from 'react'
import {pdf} from '@react-pdf/renderer'
import {saveAs} from 'file-saver'
import Plotly from 'plotly.js-dist'
import dayjs from 'dayjs'
import ChatExport from '../Export/ChatExport'
import {extractCSVFromSQLResults, getCSVFilenameFromSQL, getFormattedName, getFormattedTimestamp} from '../helpers'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'

const useExportHandlers = (
  // State values
  {messages, accounts, prismProgram},
  // State setters
  {setChartDownloadMenuAnchor, setSelectedChartMessageId, setDownloadMenuAnchor, setSelectedDownloadMessageId}
) => {
  const handleExportChatHistory = useCallback(async () => {
    const timeStamp = dayjs().format('MM/DD/YYYY [|] hh:mm:ss A')
    const allMessages = messages.map((message) => ({...message}))

    const categorySpecMessages = await Promise.all(
      allMessages
        .filter(
          (obj) => obj.responseText != null || obj.message != null || obj.graphData != null || obj.plotlyJson != null
        )
        .map(async (filteredObj) => {
          try {
            if (filteredObj.type === 'Bot' && filteredObj.plotlyJson) {
              const chartId = `sse-chart-${filteredObj.id}`
              const chartElement = document.getElementById(chartId)
              if (chartElement) {
                const dataURL = await Plotly.toImage(chartId, {
                  format: 'png',
                  width: 1000,
                  height: chartElement?.clientHeight || 400
                })
                filteredObj.graphURL = dataURL
              }
            }
          } catch (error) {
            console.error(`Error converting graph to image during PDF export`, error)
            filteredObj.graphURL = null
          }

          if (filteredObj.type === 'User' && !filteredObj.isPrompt) {
            filteredObj.label = getFormattedName(accounts[0]?.name)
          }
          return filteredObj
        })
    )

    const generatedPDF = pdf(
      <ChatExport exportTimeStamp={timeStamp} messages={categorySpecMessages} prismProgram={prismProgram} />
    )
    const blob = await generatedPDF.toBlob()
    saveAs(blob, `chatbot_log_${getFormattedTimestamp()}.pdf`)
    AnalyticsHandler.onExport({
      fileType: 'PDF',
      source: 'Chatbot'
    })
  }, [messages, accounts, prismProgram])

  const handleDownloadResponse = useCallback(
    (msgObj) => {
      let sqlExecutionResult = msgObj.sqlExecutionResult

      if (!sqlExecutionResult && msgObj.tempResponseId) {
        const textMessage = messages.find((msg) => msg.id === msgObj.tempResponseId)
        if (textMessage?.sqlExecutionResult) {
          sqlExecutionResult = textMessage.sqlExecutionResult
        }
      }

      if (!sqlExecutionResult) {
        const recentMessageWithSQL = messages
          .slice()
          .reverse()
          .find((msg) => msg.sqlExecutionResult?.results)
        if (recentMessageWithSQL) {
          sqlExecutionResult = recentMessageWithSQL.sqlExecutionResult
        }
      }

      if (sqlExecutionResult?.results) {
        const csvData = extractCSVFromSQLResults(sqlExecutionResult)

        if (csvData.length > 0) {
          const filename = getCSVFilenameFromSQL(sqlExecutionResult)

          const headers = sqlExecutionResult.column_names || Object.keys(csvData[0])
          const csvContent = [
            headers.join(','),
            ...csvData.map((row) =>
              headers
                .map((header) => `"${row[header] !== null && row[header] !== undefined ? row[header] : ''}"`)
                .join(',')
            )
          ].join('\n')

          const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
          const link = document.createElement('a')
          const url = URL.createObjectURL(blob)
          link.setAttribute('href', url)
          link.setAttribute('download', filename)
          link.style.visibility = 'hidden'
          document.body.appendChild(link)
          link.click()
          link.remove()

          AnalyticsHandler.onExport({
            fileType: 'CSV',
            source: 'Chatbot SQL Results'
          })
        }
      }
    },
    [messages]
  )

  const handleChartExport = useCallback((chartId, format = 'png') => {
    const chartElement = document.getElementById(chartId)
    if (chartElement) {
      Plotly.toImage(chartId, {format, width: chartElement.clientWidth, height: chartElement.clientHeight})
        .then((dataUrl) => {
          const link = document.createElement('a')
          link.href = dataUrl
          link.download = `chart_${getFormattedTimestamp()}.${format}`
          document.body.appendChild(link)
          link.click()
          link.remove()
        })
        .catch((error) => {
          console.error('Error exporting chart:', error)
        })
    }
  }, [])

  const handleChartDownloadMenuOpen = useCallback(
    (event, msgObj) => {
      setChartDownloadMenuAnchor(event.currentTarget)
      setSelectedChartMessageId(msgObj.id)
      event.stopPropagation()
    },
    [setChartDownloadMenuAnchor, setSelectedChartMessageId]
  )

  const handleChartDownloadMenuClose = useCallback(() => {
    setChartDownloadMenuAnchor(null)
    setSelectedChartMessageId(null)
  }, [setChartDownloadMenuAnchor, setSelectedChartMessageId])

  const handleChartDownloadSelection = useCallback(
    (format, msgObj) => {
      if (format === 'png') {
        handleChartExport(`sse-chart-${msgObj.id}`)
      } else if (format === 'csv') {
        handleDownloadResponse(msgObj)
      }
      handleChartDownloadMenuClose()
    },
    [handleChartExport, handleDownloadResponse, handleChartDownloadMenuClose]
  )

  const handleDownloadMenuOpen = useCallback(
    (event, msgObj) => {
      setDownloadMenuAnchor(event.currentTarget)
      setSelectedDownloadMessageId(msgObj.id)
      event.stopPropagation()
    },
    [setDownloadMenuAnchor, setSelectedDownloadMessageId]
  )

  const handleDownloadMenuClose = useCallback(() => {
    setDownloadMenuAnchor(null)
    setSelectedDownloadMessageId(null)
  }, [setDownloadMenuAnchor, setSelectedDownloadMessageId])

  const handleDownloadSelection = useCallback(
    (format, msgObj) => {
      if (format === 'csv') {
        handleDownloadResponse(msgObj)
      }
      handleDownloadMenuClose()
    },
    [handleDownloadResponse, handleDownloadMenuClose]
  )

  return {
    handleExportChatHistory,
    handleDownloadResponse,
    handleChartExport,
    handleChartDownloadMenuOpen,
    handleChartDownloadMenuClose,
    handleChartDownloadSelection,
    handleDownloadMenuOpen,
    handleDownloadMenuClose,
    handleDownloadSelection
  }
}
export default useExportHandlers

/*
CHATBOT EXPORT HANDLERS HOOK

KEY FUNCTIONS:
- handleExportChatHistory: Generates and downloads PDF chat history
- handleDownloadResponse: Exports SQL results as CSV files
- handleChartExport: Downloads chart visualizations as PNG
- Menu handlers: Manage download menu interactions and selections

FEATURES:
- PDF generation with formatted chat content
- CSV export from SQL query results
- Chart download with Plotly integration
- Analytics tracking for all export actions
- Menu state management for download options
*/
